<template>
  <svg
    class="water-pool-loader"
    width="178"
    height="62"
    viewBox="0 40 178 62"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <!-- The pool of water: an ellipse, like a pool seen from slightly above -->
      <clipPath :id="ids.block">
        <ellipse cx="89" cy="70" rx="86" ry="29" />
      </clipPath>
      <!-- Deeper water is darker -->
      <linearGradient :id="ids.depth" x1="0" y1="66" x2="0" y2="99" gradientUnits="userSpaceOnUse">
        <stop offset="0" style="stop-color: var(--color-primary)" />
        <stop offset="1" style="stop-color: var(--color-primary-accent)" />
      </linearGradient>
      <!-- Inner shadow: the inverted shape, blurred and shifted down a little, kept
           only inside the shape, so the edges and the surface look sunk in -->
      <filter :id="ids.inner" x="-10%" y="-10%" width="120%" height="120%">
        <feComponentTransfer in="SourceAlpha" result="outside">
          <feFuncA type="table" tableValues="1 0" />
        </feComponentTransfer>
        <feGaussianBlur in="outside" stdDeviation="7" />
        <feOffset dy="3" result="blurred" />
        <feFlood style="flood-color: var(--color-primary-accent)" flood-opacity="0.6" />
        <feComposite in2="blurred" operator="in" />
        <feComposite in2="SourceAlpha" operator="in" result="shadow" />
        <feMerge>
          <feMergeNode in="SourceGraphic" />
          <feMergeNode in="shadow" />
        </feMerge>
      </filter>
    </defs>

    <!-- Water: two repeating waves (one period = 40) drifting in opposite directions
         while the level gently rises and falls, shaded darker with depth and
         an inner shadow along every edge. The filter sits outside the clip so it works
         on the clipped shape. -->
    <g :filter="`url(#${ids.inner})`">
      <g :clip-path="`url(#${ids.block})`">
        <g class="level">
          <path
            class="water-back"
            style="fill: color-mix(in srgb, var(--color-primary) 60%, var(--color-surface-400))"
            d="M-60 69C-52.72 69 -47.28 63 -40 63C-32.72 63 -27.28 69 -20 69C-12.72 69 -7.28 63 0 63C7.28 63 12.72 69 20 69C27.28 69 32.72 63 40 63C47.28 63 52.72 69 60 69C67.28 69 72.72 63 80 63C87.28 63 92.72 69 100 69C107.28 69 112.72 63 120 63C127.28 63 132.72 69 140 69C147.28 69 152.72 63 160 63C167.28 63 172.72 69 180 69C187.28 69 192.72 63 200 63C207.28 63 212.72 69 220 69C227.28 69 232.72 63 240 63V178H-60Z"
          />
          <path
            class="water-front"
            :fill="`url(#${ids.depth})`"
            d="M-60 74C-52.72 74 -47.28 68 -40 68C-32.72 68 -27.28 74 -20 74C-12.72 74 -7.28 68 0 68C7.28 68 12.72 74 20 74C27.28 74 32.72 68 40 68C47.28 68 52.72 74 60 74C67.28 74 72.72 68 80 68C87.28 68 92.72 74 100 74C107.28 74 112.72 68 120 68C127.28 68 132.72 74 140 74C147.28 74 152.72 68 160 68C167.28 68 172.72 74 180 74C187.28 74 192.72 68 200 68C207.28 68 212.72 74 220 74C227.28 74 232.72 68 240 68V178H-60Z"
          />
          <!-- Shadow just under the surface -->
          <path
            class="water-front surface-shadow"
            style="fill: var(--color-primary-accent)"
            fill-opacity="0.12"
            d="M-60 74C-52.72 74 -47.28 68 -40 68C-32.72 68 -27.28 74 -20 74C-12.72 74 -7.28 68 0 68C7.28 68 12.72 74 20 74C27.28 74 32.72 68 40 68C47.28 68 52.72 74 60 74C67.28 74 72.72 68 80 68C87.28 68 92.72 74 100 74C107.28 74 112.72 68 120 68C127.28 68 132.72 74 140 74C147.28 74 152.72 68 160 68C167.28 68 172.72 74 180 74C187.28 74 192.72 68 200 68C207.28 68 212.72 74 220 74C227.28 74 232.72 68 240 68V178H-60Z"
          />
        </g>
      </g>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { useId } from "vue";

// Ids unique to this copy, so several loaders on one page don't borrow each other's
// clip, gradient and filter (a hidden copy would otherwise blank out the others)
const id = useId();
const ids = { block: `${id}-block`, depth: `${id}-depth`, inner: `${id}-inner` };
</script>

<style scoped>
.water-front {
  animation: drift-left 2.6s linear infinite;
}

.water-back {
  animation: drift-right 3.4s linear infinite;
}

/* The same wave a little lower, so a darker band follows just under the surface */
.surface-shadow {
  translate: 0 6px;
}

.level {
  animation: level 2.2s ease-in-out infinite alternate;
}

@keyframes drift-left {
  to {
    transform: translateX(-40px);
  }
}

@keyframes drift-right {
  to {
    transform: translateX(40px);
  }
}

@keyframes level {
  from {
    transform: translateY(3px);
  }
  to {
    transform: translateY(-3px);
  }
}
</style>
